// biome-ignore-all lint/suspicious/noArrayIndexKey: groups/parameters can repeat; composite keys use index as tiebreaker
import { cx } from '@emotion/css';
import { Alert, Button, Empty } from 'antd';
import React, { useEffect, useMemo } from 'react';
import { LoadingIndicator } from '../../../../components/LoadingIndicator.tsx';
import type { LabResultResponse } from '../../../../generated/openapi/befunde-api';
import { useLabResultViewStore } from '../../../../store/useLabResultViewStore.ts';
import { filterCumulativeGroups } from '../../../../utils/matcher.ts';
import { CumulativeLabResultObservationResultCell } from './CumulativeLabResultObservationResultCell.tsx';
import { CumulativeParameterResultsCell } from './CumulativeParameterResultsCell.tsx';
import { LabResultOrderCell } from './LabResultOrderCell.tsx';
import { useLabResultCumulativeRendererStyles } from './useLabResultCumulativeRendererStyles.ts';

export const LabResultCumulativeRenderer: React.FC<{
  result: LabResultResponse;
  deemphasizePathological: boolean;
}> = () => {
  const cumulativeLabResultState = useLabResultViewStore(state => state.cumulativeLabResultState);
  const selectedCumulativeGroups = useLabResultViewStore(state => state.selectedCumulativeGroups);
  const loadCumulativeLabResult = useLabResultViewStore(state => state.loadCumulativeLabResult);
  const valuesFilter = useLabResultViewStore(state => state.valuesFilter());
  const tokens = useLabResultViewStore(state => state.tokens);

  const countCols = cumulativeLabResultState.value?.orders?.length || 0;
  const styles = useLabResultCumulativeRendererStyles(countCols || 1);

  useEffect(() => {
    loadCumulativeLabResult(false).finally();
  }, [loadCumulativeLabResult]);

  const filteredCumulativeGroups = useMemo(
    () => filterCumulativeGroups(selectedCumulativeGroups, valuesFilter, tokens),
    [selectedCumulativeGroups, valuesFilter, tokens]
  );
  const selectedIdx = cumulativeLabResultState.value?.selectedIdx;

  if (cumulativeLabResultState.initial || cumulativeLabResultState.loading) {
    return <LoadingIndicator />;
  }
  if (cumulativeLabResultState.error) {
    return (
      <Alert
        type="error"
        title="Fehler beim Laden der Kumulativ-Daten"
        showIcon
        action={
          <Button size="small" onClick={() => loadCumulativeLabResult(true).finally()}>
            Neu laden
          </Button>
        }
      />
    );
  }
  if (!cumulativeLabResultState.value) {
    return <Empty description="Keine Kumulativdaten gefunden" image={Empty.PRESENTED_IMAGE_DEFAULT} />;
  }

  return (
    <div className={styles.content}>
      <table>
        {filteredCumulativeGroups.map((g, gidx) => (
          <React.Fragment key={`${g.group}-${gidx}`}>
            <tr>
              {/*this row is a placeholder for group. Since sticky does not work with colSoan, the group title will be placed absolute */}
              <td className={styles.groupcell} colSpan={1 + countCols}></td>
            </tr>
            <tr>
              <td className={cx(styles.firstcolumn, styles.ordergroupcell)}>
                <div className={styles.grouptitle}>{g.group}</div>
                <div className={styles.ordertitle}>Untersuchung</div>
              </td>
              {(cumulativeLabResultState.value?.orders || []).map((o, index) => (
                <td
                  key={`${g.group}-${gidx}-${o.id}`}
                  className={cx(styles.column, styles.orderentry, index === selectedIdx ? styles.selected : undefined)}
                >
                  <LabResultOrderCell order={o} />
                </td>
              ))}
            </tr>

            {g.parameters.map((p, pidx) => (
              <tr key={`${g.group}-${p.name}-${pidx}-na,e`}>
                <td className={cx(styles.firstcolumn, styles.parameter)}>
                  <CumulativeParameterResultsCell parameter={p} />
                </td>
                {p.results.map((r, ridx) => (
                  <td
                    key={`${g.group}-${p.name}-${pidx}-${ridx}`}
                    className={cx(styles.column, styles.parameter, ridx === selectedIdx ? styles.selected : undefined)}
                  >
                    <CumulativeLabResultObservationResultCell result={r} />
                  </td>
                ))}
              </tr>
            ))}
          </React.Fragment>
        ))}
      </table>
    </div>
  );
};
